import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useNavigate, useSearchParams } from 'react-router-dom'

import { Card } from '@loadsmart/miranda-react'

import { LOGIN_STORAGE_USER, TOKEN_STORAGE_USER_KEY } from 'auth/domain/User'
import { useOpendockLogin } from 'auth/ui/hooks/useOpendockLogin'
import { AnalyticsEvent } from 'core/analytics/events'
import useAnalytics from 'core/analytics/useAnalytics'
import { LocalStorageClient } from 'core/infra/storage/LocalStorageClient'

import OpenDockLoginForm from './OpenDockLoginForm'
import { StyledCardDiv } from './LoginPage.styles'

const OpenDockLoginPage = () => {
  const [error, setError] = useState('')
  const navigate = useNavigate()
  const { track } = useAnalytics()
  const hasAuthenticatedRef = useRef(false)
  const hasStartedAuthRef = useRef(false)
  const abandonTrackedRef = useRef(false)
  const authTrackedRef = useRef(false)
  const startTrackedRef = useRef(false)

  // TODO: see why useAbortController usage is failing
  const abortController = useMemo(() => new AbortController(), [])

  const { isLoading, login: opendockLogin } = useOpendockLogin()

  const storage = new LocalStorageClient()
  const userAccessToken = storage.get(TOKEN_STORAGE_USER_KEY)

  const [searchParams] = useSearchParams()

  const redirectTo = searchParams.get('redirect_to')

  function getUserData() {
    try {
      const userData = storage.get<string | { email: string; [key: string]: unknown }>(LOGIN_STORAGE_USER)
      if (!userData) return null

      let user: { email: string; [key: string]: unknown }
      if (typeof userData === 'string') {
        user = JSON.parse(userData)
      } else {
        user = userData
      }

      return {
        email: user.email,
      }
    } catch {
      return null
    }
  }

  // Track when login page/form is opened
  useEffect(() => {
    if (!startTrackedRef.current) {
      startTrackedRef.current = true
      const userData = getUserData()
      track(AnalyticsEvent.Authentication, { 
        authFlow: 'OpenDock', 
        authMethod: 'AuthForm', 
        authStage: 'start',
        ...(userData ? { email: userData.email } : {})
      })
    }
  }, [track])

  useEffect(() => {
    if (userAccessToken) {
      hasAuthenticatedRef.current = true
      navigate(redirectTo ?? '/demo')
    }
  }, [navigate, userAccessToken, redirectTo])

  // Track abandon if component unmounts or user navigates away without successful authentication
  useEffect(() => {
      const handleAbandon = () => {
        if (startTrackedRef.current && !hasAuthenticatedRef.current && !abandonTrackedRef.current) {
          abandonTrackedRef.current = true
          const userData = getUserData()
          track(AnalyticsEvent.Authentication, { 
            authFlow: 'OpenDock', 
            authMethod: 'AuthForm', 
            authStage: 'abandon',
            ...(userData ? { email: userData.email } : {})
          })
        }
      }

      // Track on page visibility change (user switches tabs/windows)
      const handleVisibilityChange = () => {
        if (document.hidden && startTrackedRef.current && !hasAuthenticatedRef.current && !abandonTrackedRef.current) {
          abandonTrackedRef.current = true
          const userData = getUserData()
          track(AnalyticsEvent.Authentication, { 
            authFlow: 'OpenDock', 
            authMethod: 'AuthForm', 
            authStage: 'abandon',
            ...(userData ? { email: userData.email } : {})
          })
        }
      }

      // Track on page unload (user navigates away/closes tab)
      const handleBeforeUnload = () => {
        if (startTrackedRef.current && !hasAuthenticatedRef.current && !abandonTrackedRef.current) {
          abandonTrackedRef.current = true
          const userData = getUserData()
          track(AnalyticsEvent.Authentication, { 
            authFlow: 'OpenDock', 
            authMethod: 'AuthForm', 
            authStage: 'abandon',
            ...(userData ? { email: userData.email } : {})
          })
        }
      }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      handleAbandon()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [track])

  const handleOpendockLogin = useCallback(
    async (email: string, password: string, captchaToken: string) => {
      setError('')
      hasStartedAuthRef.current = true

      try {
        await opendockLogin(email, password, captchaToken, abortController)
        hasAuthenticatedRef.current = true
        if (!authTrackedRef.current) {
          authTrackedRef.current = true
          const userData = getUserData()
          track(AnalyticsEvent.Authentication, { 
            authFlow: 'OpenDock', 
            authMethod: 'AuthForm', 
            authStage: 'success',
            ...(userData ? { email: userData.email } : {})
          })
        }
        navigate(redirectTo ?? '/demo')
      } catch (error: any) {
        // Handle different error response formats
        let errorMessage = 'Login failed. Please check your credentials.'
        
        // Handle CORS errors
        if (error?.code === 'ERR_NETWORK' || error?.message?.includes('CORS') || error?.message?.includes('Access-Control')) {
          errorMessage = 'Network error: Unable to connect to the server. Please check if the backend is running and CORS is configured.'
        }
        // Handle HTTP errors with response data
        else if (error?.response?.data) {
          const errorData = error.response.data
          if (errorData.detail) {
            errorMessage = errorData.detail
          } else if (errorData.email || errorData.password) {
            // Handle validation errors
            const validationErrors = []
            if (errorData.email) validationErrors.push(`Email: ${Array.isArray(errorData.email) ? errorData.email.join(', ') : errorData.email}`)
            if (errorData.password) validationErrors.push(`Password: ${Array.isArray(errorData.password) ? errorData.password.join(', ') : errorData.password}`)
            errorMessage = validationErrors.join('; ')
          }
        } 
        // Handle HTTP errors without response data (e.g., 500 errors)
        else if (error?.response?.status) {
          if (error.response.status === 500) {
            errorMessage = 'Server error: The server encountered an error. Please try again later or contact support.'
          } else if (error.response.status === 401) {
            errorMessage = 'Invalid credentials. Please check your email and password.'
          } else if (error.response.status === 400) {
            errorMessage = 'Invalid request. Please check your input.'
          }
        }
        // Handle other error messages
        else if (error?.message) {
          errorMessage = error.message
        }
        
        const userData = getUserData()
        track(AnalyticsEvent.Authentication, { 
          authFlow: 'OpenDock',
          authMethod: 'AuthForm',
          authStage: 'failure',
          error: errorMessage,
          ...(userData ? { email: userData.email } : {})
        })
        setError(errorMessage)
      }
    },
    [abortController, opendockLogin, navigate, redirectTo, track]
  )

  return (
    <StyledCardDiv>
      <Card>
        <Card.Title>Sign in to Loadsmart's Managed Analytics</Card.Title>
        <Card.Divider></Card.Divider>
        <Card.Body>
          {error && <p>{error}</p>}
          <OpenDockLoginForm onSubmit={handleOpendockLogin} isLoading={isLoading} />
        </Card.Body>
      </Card>
    </StyledCardDiv>
  )
}

export default OpenDockLoginPage
